import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Note } from "@material-ui/icons";
import { PDFDownloadLink, PDFViewer, pdf } from "@react-pdf/renderer";
import { BoxButton, CDialog, CRadioGroup, CTextField, Icon } from "shared";
import { useCareEvents } from "routes/patient/queries/useCareEvents";
import usePatient from "routes/patient/queries/usePatient";
import { Button, TextField, CircularProgress } from "@material-ui/core";
import { useUkDischargeLetter } from "./useUkDischargeLetter";
import { DischargeLetter } from "./components";
import styled from "styled-components";
import { FormProvider, useForm } from "react-hook-form";
import api from "api/api";

const defaultValues = {
  patient_address: "",
  nhs_number: "",
  differential_diagnosis: "",
  discharge_urgency: "",
  discharge_further_action: "",
  discharge_patient_f2f: "",
  discharge_self_management: "",
  discharge_gp_action: "",
  discharge_advanced_physio: "",
  discharge_physio_f2f: "",
  discharge_other_text: "",
  plan: "",
  subjective: "",
  red_flags: "",
  past_medical_history: "",
  medication: "",
  objective: "",
  inspection_v2: "",
  palpation_v2: "",
  neurology_v2: "",
  joint_functionality_v2: "",
  functional_tests_v2: "",
  treatment_programme: "",
  initial_eq5d: "",
  latest_eq5d: "",
  adherence_measure: "",
  status: "",
  goal_free_text: "",
  goal_free_text_achieved: "",
  goal_functionality: "",
  goal_functionality_achieved: "",
  goal_pain: "",
  goal_pain_achieved: "",
  discharge_note_to_patient: "",
  gp_address: "",
};

export const UkDischargeLetter: React.VFC = () => {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(false);
  const closeDialog = () => setOpen(false);
  const openDialog = () => setOpen(true);
  const { data: patient } = usePatient();
  const { data: careEvents } = useCareEvents();
  const { data: ukDischargeLetter } = useUkDischargeLetter();

  const [dischargeNote, setDischargeNote] = useState<any>({});
  const [site, setSite] = useState("");
  const [hasAnsweredFollowUpHealthReport, setHasAnsweredFollowUpHealthReport] = useState(false);

  const form = useForm({
    defaultValues,
    mode: "onChange",
  });

  useEffect(() => {
    const kickoffCe = careEvents?.find(
      careEvent => careEvent.label === "protocol_weekly_w0" && careEvent.review_state === "signed"
    );
    const dischargeCe = careEvents?.find(
      careEvent => careEvent.label === "discharge" && careEvent.review_state === "signed"
    );
    // dischargeCe might not exist
    const dischargeNote = { ...kickoffCe, ...dischargeCe };
    const formData = { ...defaultValues };

    if (dischargeNote) {
      Object.assign(formData, {
        patient_address: `${patient?.address}\n${patient?.city}\n${patient?.zip_code}`,
        nhs_number: patient?.nhs_number || "",
        differential_diagnosis: dischargeNote?.medical_record_note?.differential_diagnosis || "",
        discharge_urgency: dischargeNote?.medical_record_note?.discharge_urgency,
        discharge_further_action: convertBooleanToString(dischargeNote?.medical_record_note?.discharge_further_action),
        discharge_patient_f2f: convertBooleanToString(dischargeNote?.medical_record_note?.discharge_patient_f2f),
        discharge_self_management: convertBooleanToString(
          dischargeNote?.medical_record_note?.discharge_self_management
        ),
        discharge_gp_action: convertBooleanToString(dischargeNote?.medical_record_note?.discharge_gp_action),
        discharge_advanced_physio: convertBooleanToString(
          dischargeNote?.medical_record_note?.discharge_advanced_physio
        ),
        discharge_physio_f2f: convertBooleanToString(dischargeNote?.medical_record_note?.discharge_physio_f2f),
        discharge_other_text: dischargeNote?.medical_record_note?.discharge_other_text || "",
        plan: dischargeNote?.medical_record_note?.plan || "",
        subjective: dischargeNote?.medical_record_note?.subjective,
        red_flags: dischargeNote?.medical_record_note?.red_flags || "",
        past_medical_history: dischargeNote?.medical_record_note?.past_medical_history || "",
        medication: dischargeNote?.medical_record_note?.medication || "",
        objective: dischargeNote?.medical_record_note?.objective || "",
        inspection_v2: dischargeNote?.medical_record_note?.inspection_v2 || "",
        palpation_v2: dischargeNote?.medical_record_note?.palpation_v2 || "",
        neurology_v2: dischargeNote?.medical_record_note?.neurology_v2 || "",
        joint_functionality_v2: dischargeNote?.medical_record_note?.joint_functionality_v2 || "",
        functional_tests_v2: dischargeNote?.medical_record_note?.functional_tests_v2 || "",
        adherence_measure: dischargeNote?.medical_record_note?.adherence_measure,
        status: dischargeNote?.medical_record_note?.status || "",
        goal_free_text: dischargeNote?.medical_record_note?.goal_free_text,
        goal_free_text_achieved: dischargeNote?.medical_record_note?.goal_free_text_achieved || "",
        goal_functionality: dischargeNote?.medical_record_note?.goal_functionality?.toString(),
        goal_functionality_achieved: dischargeNote?.medical_record_note?.goal_functionality_achieved || "",
        goal_pain: dischargeNote?.medical_record_note?.goal_pain?.toString(),
        goal_pain_achieved: dischargeNote?.medical_record_note?.goal_pain_achieved || "",
        discharge_note_to_patient: dischargeNote?.medical_record_note?.discharge_note_to_patient || "",
      });
    }

    if (kickoffCe && dischargeCe) {
      // if both exist pull plan from treatment_programme to kickoff_ce
      Object.assign(formData, {
        treatment_programme:
          kickoffCe?.medical_record_note?.plan?.replace("Insert any other specific advice.", "") || "",
        // these only exist/editable on kickoff_ce
        inspection_v2: kickoffCe?.medical_record_note?.inspection_v2 || "",
        palpation_v2: kickoffCe?.medical_record_note?.palpation_v2 || "",
        neurology_v2: kickoffCe?.medical_record_note?.neurology_v2 || "",
        joint_functionality_v2: kickoffCe?.medical_record_note?.joint_functionality_v2 || "",
        functional_tests_v2: kickoffCe?.medical_record_note?.functional_tests_v2 || "",
      });
    }
    setDischargeNote({ ...dischargeNote, kickoff_date: kickoffCe?.start_time });

    if (ukDischargeLetter) {
      Object.assign(formData, {
        // @ts-ignore
        initial_eq5d: ukDischargeLetter?.initial_eq5d,
        // @ts-ignore
        latest_eq5d: ukDischargeLetter?.latest_eq5d,
      });
    }

    form.reset(formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [careEvents, patient, ukDischargeLetter]);

  useEffect(() => {
    // latest_eq5d is taken from the follow up health report as seen here: https://github.com/Jojnts/jojnts-service/blob/develop/app/concepts/care_events/protocol/template_parameters.rb#L297
    // this is used as an indicator if the patient filled out the follow up health report or not
    // @ts-ignore
    if (ukDischargeLetter?.latest_eq5d && ukDischargeLetter.latest_eq5d !== "{last_eq5d}") {
      setHasAnsweredFollowUpHealthReport(true);
    } else {
      setHasAnsweredFollowUpHealthReport(false);
    }
  }, [ukDischargeLetter]);

  const onCreateLetter = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const getFileName = () => {
    return `${form.getValues("nhs_number")} + ${patient?.first_name?.[0]}${
      patient?.last_name?.[0]
    } JA/CIC ${site} Discharge Letter`;
  };

  const convertBooleanToString = (value?: boolean) => {
    if (value) {
      return "true";
    }
    return "false";
  };

  const convertStringToBoolean = (value: string) => {
    if (value === "true") {
      return true;
    }
    return false;
  };

  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const uploadDischargeLetter = async () => {
    if (!patient?.id) return;

    // Confirm if overwriting existing discharge letter
    if (hasExistingDischargeLetter) {
      const confirmed = window.confirm(
        "A discharge letter has already been uploaded for this patient. Do you want to replace it with a new version?"
      );
      if (!confirmed) return;
    }

    setIsUploading(true);
    setUploadSuccess(false);

    try {
      // Generate PDF blob
      const blob = await pdf(getDischargeLetter()).toBlob();

      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = async () => {
        const base64data = reader.result?.toString().split(",")[1];

        await api(`POST /patients/{patient_id}/create_discharge_letter`, {
          params: [patient.id],
          data: {
            pdf: base64data || "",
          },
        });

        setUploadSuccess(true);
        alert(
          hasExistingDischargeLetter
            ? "Discharge letter successfully updated!"
            : "Discharge letter successfully uploaded!"
        );
        // Refetch to get updated discharge letter data
        window.location.reload();
      };
    } catch (error) {
      console.error("Failed to upload discharge letter:", error);
      alert("Failed to upload discharge letter. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const hasExistingDischargeLetter = !!ukDischargeLetter?.discharge_letter_url;

  const getDischargeLetter = () => {
    return (
      <DischargeLetter
        patient={{
          first_name: patient?.first_name || "",
          last_name: patient?.last_name || "",
          birthday: patient?.birthday || "",
          address: form.watch("patient_address") || "",
          phone_number: patient?.phone_number || "",
          nhs_number: form.watch("nhs_number") || "",
          email: patient?.email || "",
          therapist: {
            first_name: patient?.therapist?.first_name || "",
            last_name: patient?.therapist?.last_name || "",
          },
        }}
        kickoff_date={dischargeNote?.kickoff_date}
        bmi={dischargeNote?.medical_record_note?.bmi}
        differential_diagnosis={form.watch("differential_diagnosis")}
        signed_at={dischargeNote?.signed_at}
        discharge_urgency={form.watch("discharge_urgency")}
        discharge_further_action={convertStringToBoolean(form.watch("discharge_further_action"))}
        discharge_patient_f2f={convertStringToBoolean(form.watch("discharge_patient_f2f"))}
        discharge_self_management={convertStringToBoolean(form.watch("discharge_self_management"))}
        discharge_gp_action={convertStringToBoolean(form.watch("discharge_gp_action"))}
        discharge_advanced_physio={convertStringToBoolean(form.watch("discharge_advanced_physio"))}
        discharge_physio_f2f={convertStringToBoolean(form.watch("discharge_physio_f2f"))}
        discharge_other_text={form.watch("discharge_other_text")}
        plan={form.watch("plan")}
        subjective={form.watch("subjective")}
        red_flags={form.watch("red_flags")}
        past_medical_history={form.watch("past_medical_history")}
        medication={form.watch("medication")}
        objective={form.watch("objective")}
        inspection_v2={form.watch("inspection_v2")}
        palpation_v2={form.watch("palpation_v2")}
        neurology_v2={form.watch("neurology_v2")}
        joint_functionality_v2={form.watch("joint_functionality_v2")}
        functional_tests_v2={form.watch("functional_tests_v2")}
        treatment_programme={form.watch("treatment_programme")}
        adherence_measure={form.watch("adherence_measure")}
        status={form.watch("status")}
        goal_free_text={form.watch("goal_free_text")}
        goal_free_text_achieved={form.watch("goal_free_text_achieved")}
        goal_functionality={form.watch("goal_functionality")}
        goal_functionality_achieved={form.watch("goal_functionality_achieved")}
        goal_pain={form.watch("goal_pain")}
        goal_pain_achieved={form.watch("goal_pain_achieved")}
        discharge_note_to_patient={form.watch("discharge_note_to_patient")}
        ukDischargeLetter={ukDischargeLetter}
        initialEQ5D={form.watch("initial_eq5d")}
        latestEQ5D={form.watch("latest_eq5d")}
        gp_address={form.watch("gp_address")}
        hasAnsweredFollowUpHealthReport={hasAnsweredFollowUpHealthReport}
      />
    );
  };

  return (
    <>
      <BoxButton startIcon={<Icon of={Note} />} onClick={openDialog}>
        {t("patients.generate_uk_discharge_letter")}
      </BoxButton>

      <CDialog
        disableBackdropClick
        disableEscapeKeyDown
        fullScreen
        open={isOpen}
        onClose={closeDialog}
        aria-labelledby="generate-uk-discharge-letter"
      >
        <div style={{ display: "flex", height: "100%" }}>
          <div style={{ display: "flex", flexDirection: "column", minWidth: "400px", padding: "0 8px" }}>
            <FormProvider {...form}>
              <form
                onSubmit={onCreateLetter}
                style={{ display: "flex", flexDirection: "column", marginBottom: "16px" }}
              >
                <StyledCTextField name="patient_address" label={t("patients.uk_discharge_letter.address")} multiline />
                <StyledCTextField name="nhs_number" label={t("patients.uk_discharge_letter.nhs_number")} />
                <StyledCTextField
                  name="differential_diagnosis"
                  label={t("patients.uk_discharge_letter.differential_diagnosis")}
                  multiline
                />
                <CRadioGroup
                  name="discharge_urgency"
                  label={t("patients.uk_discharge_letter.discharge_urgency")}
                  row
                  items={[
                    { label: t("patients.uk_discharge_letter.discharge_urgency_options.routine"), value: "routine" },
                    { label: t("patients.uk_discharge_letter.discharge_urgency_options.urgent"), value: "urgent" },
                  ]}
                />
                <CRadioGroup
                  name="discharge_further_action"
                  label={t("patients.uk_discharge_letter.discharge_further_action")}
                  row
                  items={[
                    { label: t("patients.uk_discharge_letter.discharge_further_action"), value: "true" },
                    { label: t("patients.uk_discharge_letter.discharge_no_further_action"), value: "false" },
                  ]}
                />
                <CRadioGroup
                  name="discharge_patient_f2f"
                  label={t("patients.uk_discharge_letter.discharge_patient_f2f")}
                  row
                  items={[
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                  ]}
                />
                <CRadioGroup
                  name="discharge_self_management"
                  label={t("patients.uk_discharge_letter.discharge_self_management")}
                  row
                  items={[
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                  ]}
                />
                <CRadioGroup
                  name="discharge_gp_action"
                  label={t("patients.uk_discharge_letter.discharge_gp_action")}
                  row
                  items={[
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                  ]}
                />
                <CRadioGroup
                  name="discharge_advanced_physio"
                  label={t("patients.uk_discharge_letter.discharge_advanced_physio")}
                  row
                  items={[
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                  ]}
                />
                <CRadioGroup
                  name="discharge_physio_f2f"
                  label={t("patients.uk_discharge_letter.discharge_physio_f2f")}
                  row
                  items={[
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                  ]}
                />
                <StyledCTextField
                  name="discharge_other_text"
                  label={t("patients.uk_discharge_letter.discharge_other")}
                  multiline
                />
                <StyledCTextField name="plan" label={t("patients.uk_discharge_letter.plan")} multiline />
                <StyledCTextField name="subjective" label={t("patients.uk_discharge_letter.subjective")} multiline />
                <StyledCTextField name="red_flags" label={t("patients.uk_discharge_letter.red_flags")} multiline />
                <StyledCTextField
                  name="past_medical_history"
                  label={t("patients.uk_discharge_letter.past_medical_history")}
                  multiline
                />
                <StyledCTextField name="medication" label={t("patients.uk_discharge_letter.medication")} multiline />
                <StyledCTextField name="objective" label={t("patients.uk_discharge_letter.objective")} multiline />
                <StyledCTextField
                  name="inspection_v2"
                  label={t("patients.uk_discharge_letter.inspection_v2")}
                  multiline
                />
                <StyledCTextField
                  name="palpation_v2"
                  label={t("patients.uk_discharge_letter.palpation_v2")}
                  multiline
                />
                <StyledCTextField
                  name="neurology_v2"
                  label={t("patients.uk_discharge_letter.neurology_v2")}
                  multiline
                />
                <StyledCTextField
                  name="joint_functionality_v2"
                  label={t("patients.uk_discharge_letter.joint_functionality_v2")}
                  multiline
                />
                <StyledCTextField
                  name="functional_tests_v2"
                  label={t("patients.uk_discharge_letter.functional_tests_v2")}
                  multiline
                />
                <StyledCTextField
                  name="treatment_programme"
                  label={t("patients.uk_discharge_letter.treatment_programme")}
                  multiline
                />
                <StyledCTextField name="initial_eq5d" label={t("patients.uk_discharge_letter.table.initial_eq5d")} />
                <StyledCTextField name="latest_eq5d" label={t("patients.uk_discharge_letter.table.latest_eq5d")} />
                <StyledCTextField name="adherence_measure" label={t("patients.uk_discharge_letter.table.adherence")} />
                <StyledCTextField name="status" label={t("patients.uk_discharge_letter.management")} multiline />
                <GoalGroup>
                  <StyledCTextField name="goal_pain" label={t("patients.uk_discharge_letter.goal_pain")} />
                  <CRadioGroup
                    name="goal_pain_achieved"
                    label={t("patients.uk_discharge_letter.goal_pain")}
                    row
                    items={[
                      { label: t("patients.uk_discharge_letter.goal_achieved"), value: "achieved" },
                      { label: t("patients.uk_discharge_letter.goal_not_achieved"), value: "not_achieved" },
                      { label: t("patients.uk_discharge_letter.goal_partially_achieved"), value: "partially_achieved" },
                    ]}
                  />
                </GoalGroup>
                <GoalGroup>
                  <StyledCTextField
                    name="goal_functionality"
                    label={t("patients.uk_discharge_letter.goal_functionality")}
                  />
                  <CRadioGroup
                    name="goal_functionality_achieved"
                    label={t("patients.uk_discharge_letter.goal_functionality")}
                    row
                    items={[
                      { label: t("patients.uk_discharge_letter.goal_achieved"), value: "achieved" },
                      { label: t("patients.uk_discharge_letter.goal_not_achieved"), value: "not_achieved" },
                      { label: t("patients.uk_discharge_letter.goal_partially_achieved"), value: "partially_achieved" },
                    ]}
                  />
                </GoalGroup>
                <GoalGroup>
                  <StyledCTextField
                    name="goal_free_text"
                    label={t("patients.uk_discharge_letter.goal_free_text")}
                    multiline
                  />
                  <CRadioGroup
                    name="goal_free_text_achieved"
                    label={t("patients.uk_discharge_letter.goal_free_text")}
                    row
                    items={[
                      { label: t("patients.uk_discharge_letter.goal_achieved"), value: "achieved" },
                      { label: t("patients.uk_discharge_letter.goal_not_achieved"), value: "not_achieved" },
                      { label: t("patients.uk_discharge_letter.goal_partially_achieved"), value: "partially_achieved" },
                    ]}
                  />
                </GoalGroup>
                <StyledCTextField
                  name="discharge_note_to_patient"
                  label={t("patients.uk_discharge_letter.discharge_note_to_patient")}
                  multiline
                />
                <StyledCTextField name="gp_address" label="GP Address" multiline />
              </form>
            </FormProvider>

            <StyledTextField name="site" label="Site" onChange={e => setSite(e.target.value)} />
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div
                style={{
                  padding: "12px",
                  backgroundColor: patient?.discharge_email_consent ? "#e8f5e9" : "#fff3e0",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              >
                <strong>Patient discharge email consent:</strong> {patient?.discharge_email_consent ? "Yes ✓" : "No"}
              </div>

              {hasExistingDischargeLetter && ukDischargeLetter?.discharge_letter_uploaded_at && (
                <div style={{ padding: "12px", backgroundColor: "#e3f2fd", borderRadius: "4px", fontSize: "14px" }}>
                  <strong>Patient discharge letter last uploaded:</strong>{" "}
                  {new Date(ukDischargeLetter.discharge_letter_uploaded_at).toLocaleString()}
                  {ukDischargeLetter?.discharge_letter_url && (
                    <>
                      {" - "}
                      <a
                        href={ukDischargeLetter.discharge_letter_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1976d2", textDecoration: "underline", cursor: "pointer" }}
                      >
                        View
                      </a>
                    </>
                  )}
                </div>
              )}
              <Button
                variant="contained"
                color="secondary"
                style={{ width: "100%" }}
                onClick={uploadDischargeLetter}
                disabled={isUploading}
              >
                {isUploading ? (
                  <CircularProgress size={24} />
                ) : uploadSuccess ? (
                  "PDF Updated ✓"
                ) : (
                  "Generate discharge letter to patient"
                )}
              </Button>
              <PDFDownloadLink document={getDischargeLetter()} fileName={getFileName()}>
                <Button variant="contained" color="primary" style={{ width: "100%" }}>
                  {t("patients.uk_discharge_letter.download")}
                </Button>
              </PDFDownloadLink>
            </div>
          </div>

          <PDFViewer height="100%" width="100%" showToolbar={false}>
            {getDischargeLetter()}
          </PDFViewer>
        </div>
      </CDialog>
    </>
  );
};

const StyledTextField = styled(TextField)`
  margin-bottom: 16px;
`;

const StyledCTextField = styled(CTextField)`
  margin-bottom: 16px;
`;

const GoalGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 32px;
`;
